// The Inference pages read InferenceEndpoints as the API serves them (resources.md §4.5,
// api/nodus/v1/inferenceendpoint_types.go) and the model catalog and request receipts from the inference data plane
// (`GET /v1/models`, `GET /v1/requests/{id}`). These are the fields the pages show; everything else stays in View YAML.
import type { Condition, KObject } from "@nodus/api";
import { formatMoney, parseMicros } from "@nodus/ui";
import type { RuntimeConfig } from "../../config";

export type Scope = { org: string; project: string };

export type EndpointLimits = { rpm?: number; tpm?: number; maxConcurrent?: number };

export type EndpointSpec = {
  model?: string;
  limits?: EndpointLimits;
  allowedKeys?: string[];
  maxCostUSD?: string;
  state?: "Running" | "Stopped";
};

export type EndpointUsage = {
  requests: number;
  errors: number;
  inputTokens: number;
  outputTokens: number;
  costUSD: string;
  openHolds: number;
};

export type EndpointStatus = {
  phase?: string;
  reason?: string;
  message?: string;
  model?: string;
  conditions?: Condition[];
  usage24h?: EndpointUsage;
};

export type InferenceEndpoint = KObject<EndpointSpec, EndpointStatus>;

/** The model of `GET /v1/models`: the OpenAI model object with the catalog's display fields. */
export type CatalogModel = {
  id: string;
  owned_by?: string;
  display_name?: string;
  description?: string;
  operations?: string[];
  aliases?: string[];
  context_window?: number;
  max_output_tokens?: number;
  preview?: boolean;
  capabilities?: Partial<Record<Capability, boolean>>;
  /** Customer rates in USD: per million tokens, per audio hour, per million characters. Absent on Indra. */
  pricing?: { input?: string; cache_read?: string; output?: string; audio_hour?: string; million_characters?: string };
};

/** The capability chips and filters, in display order. */
export const CAPABILITIES = ["vision", "tools", "reasoning", "decisions", "audio", "embeddings"] as const;
export type Capability = (typeof CAPABILITIES)[number];
export const CAPABILITY_LABEL: Record<Capability, string> = { vision: "Vision", tools: "Tools", reasoning: "Reasoning", decisions: "Decisions", audio: "Audio", embeddings: "Embeddings" };

/** Parses `?caps=vision,tools`, keeping known capabilities only. */
export function parseCaps(value: string | undefined): Capability[] {
  const set = new Set((value ?? "").split(","));
  return CAPABILITIES.filter((c) => set.has(c));
}

export function hasCaps(m: CatalogModel, caps: Capability[]): boolean {
  return caps.every((c) => {
    if (c === "decisions") return m.operations?.includes("Decisions");
    if (c === "embeddings") return m.operations?.includes("Embeddings");
    if (c === "audio") return modelKind(m) === "speech" || modelKind(m) === "transcription";
    return m.capabilities?.[c];
  });
}

const LABS: Record<string, string> = {
  nodus: "Nodus",
  openai: "OpenAI",
  qwen: "Qwen",
  "zai-org": "Z.ai",
  moonshotai: "Moonshot AI",
  "deepseek-ai": "DeepSeek",
  canopylabs: "Canopy Labs",
  "meta-llama": "Meta",
  google: "Google",
  mistralai: "Mistral AI",
};

/** The lab that publishes the model, from its id's namespace. */
export function labOf(id: string): string {
  const ns = id.includes("/") ? id.slice(0, id.indexOf("/")) : "";
  return LABS[ns] ?? ns;
}

const KIND_ORDER = { chat: 0, decisions: 0.5, embeddings: 1, transcription: 2, speech: 3 } as const;

/** Indra first, then chat models before audio ones, each by lab and name: the order the grid shows. */
export function compareModels(a: CatalogModel, b: CatalogModel): number {
  if (isRouted(a.id) !== isRouted(b.id)) return isRouted(a.id) ? -1 : 1;
  return (
    KIND_ORDER[modelKind(a)] - KIND_ORDER[modelKind(b)] ||
    labOf(a.id).localeCompare(labOf(b.id)) ||
    modelTitle(a).localeCompare(modelTitle(b), undefined, { numeric: true })
  );
}

/** The model's operation, with pricing as a fallback for older catalog responses. */
export function modelKind(m: CatalogModel): "chat" | "decisions" | "embeddings" | "transcription" | "speech" {
  if (m.operations?.includes("Decisions")) return "decisions";
  if (m.operations?.includes("Embeddings")) return "embeddings";
  if (m.operations?.includes("AudioTranscriptions") || m.pricing?.audio_hour) return "transcription";
  if (m.operations?.includes("AudioSpeech") || m.pricing?.million_characters) return "speech";
  return "chat";
}

export type ModelList = { object: "list"; data: CatalogModel[] };

/** `GET /v1/requests/{id}`: a request's receipt, kept for 30 days. */
export type Receipt = {
  id: string;
  model: string;
  routedModel?: string;
  operation: string;
  project: string;
  endpoint?: string;
  state: "Running" | "Settled" | "Released" | "Unknown";
  stream: boolean;
  createdAt: string;
  finishedAt?: string;
  usage?: { inputTokens: number; outputTokens: number; cacheReadTokens?: number };
  heldUSD?: string;
  amountUSD: string;
  pricebookVersion?: string;
};

/** The defaults and bounds the server applies (resources.md §4.5). */
export const LIMIT_DEFAULTS = { rpm: 60, maxConcurrent: 4 } as const;
export const LIMIT_BOUNDS = { rpm: 100_000, maxConcurrent: 256 } as const;
export const MAX_ALLOWED_KEYS = 100;

/** The routed model: Indra picks the catalog model best suited to each request. `nodus/auto` is its alias. */
export const ROUTED_MODEL = "nodus/indra";
const ROUTED_IDS: ReadonlySet<string | undefined> = new Set([ROUTED_MODEL, "nodus/auto", "composer", "nodus:auto"]);

export function isRouted(modelId: string | undefined): boolean {
  return ROUTED_IDS.has(modelId);
}

export function limitsOf(e: InferenceEndpoint): { rpm: number; tpm?: number; maxConcurrent: number } {
  const l = e.spec?.limits ?? {};
  return {
    rpm: l.rpm ?? LIMIT_DEFAULTS.rpm,
    ...(l.tpm !== undefined ? { tpm: l.tpm } : {}),
    maxConcurrent: l.maxConcurrent ?? LIMIT_DEFAULTS.maxConcurrent,
  };
}

/** The catalog model the endpoint serves, as the status resolved it, else as the spec names it. */
export function modelOf(e: InferenceEndpoint): string {
  return e.status?.model || e.spec?.model || "—";
}

export function isStopped(e: InferenceEndpoint): boolean {
  return e.spec?.state === "Stopped";
}

/** Why a Running endpoint answers 503: its model cannot serve now. */
export function modelProblem(e: InferenceEndpoint): { reason: string; message?: string } | undefined {
  if (isStopped(e)) return undefined;
  const c = e.status?.conditions?.find((x) => x.type === "Ready" && x.status === "False");
  return c ? { reason: c.reason ?? "ModelUnavailable", ...(c.message ? { message: c.message } : {}) } : undefined;
}

/**
 * The status display inputs of an endpoint. Until the controller writes a status it is Pending, and once Stopped its
 * phase already says so, so the Stopped reason is not repeated.
 */
export function statusProps(e: InferenceEndpoint): { kind: string; phase: string; reason?: string; message?: string } {
  const { phase, reason, message } = e.status ?? {};
  return {
    kind: "InferenceEndpoint",
    phase: phase || "Pending",
    ...(reason && reason !== "Stopped" ? { reason } : {}),
    ...(message ? { message } : {}),
  };
}

export function allowedKeysLabel(e: InferenceEndpoint): string {
  const keys = e.spec?.allowedKeys ?? [];
  return keys.length ? keys.join(", ") : "Any key with inference:invoke";
}

/** Dollars for the charges of per-token requests: cents as usual, but an amount under a cent keeps its digits. */
export function usd(value: string | undefined): string {
  const micros = parseMicros(value);
  if (micros === undefined) return "—";
  return micros > 0 && micros < 10_000 ? `$${(micros / 1_000_000).toFixed(6).replace(/0+$/, "")}` : formatMoney(value);
}

export function limitLabel(value: number | undefined, unit: string): string {
  return value === undefined ? "No limit" : `${value.toLocaleString("en-US")} ${unit}`;
}

/** "128K": a context window or output bound in the units the catalog quotes. */
export function tokenCountLabel(n: number): string {
  if (n >= 1_000_000) return `${Math.round(n / 100_000) / 10}M`;
  return n >= 1024 ? `${Math.round(n / 1024)}K` : String(n);
}

/** The model's display name, else its id. The routed model is always Indra, under either of its ids. */
export function modelTitle(m: CatalogModel): string {
  return isRouted(m.id) ? "Indra" : m.display_name || m.id;
}

/** What goes in and comes out: "text → text", "text + image → text", "audio → text", "text → audio". */
export function modalityLabel(m: CatalogModel): string {
  const kind = modelKind(m);
  if (kind === "decisions") return "text → decisions";
  if (kind === "embeddings") return "text → vectors";
  if (kind === "transcription") return "audio → text";
  if (kind === "speech") return "text → audio";
  return m.capabilities?.vision ? "text + image → text" : "text → text";
}

/** The API the model answers on, as the data plane serves it. */
export function apiLabel(m: CatalogModel): string {
  const kind = modelKind(m);
  if (kind === "decisions") return "decisions";
  if (kind === "embeddings") return "embeddings";
  return kind === "transcription" ? "audio transcriptions" : kind === "speech" ? "audio speech" : "chat completions";
}

/** A rate at card precision: cents from a cent ("$0.06", "$10.29"), one significant digit below it ("$0.002"). */
function cardPrice(n: number): string {
  if (n === 0) return "Free";
  return n >= 0.01 ? `$${n.toFixed(2)}` : `$${Number(n.toPrecision(1))}`;
}

/** Model cards label both token directions; audio and embedding rates retain their own units. */
export function cardPriceLabel(m: CatalogModel): string | undefined {
  const p = m.pricing;
  const n = (v: string | undefined) => (v === undefined ? NaN : Number(v));
  if (!p) return undefined;
  if (modelKind(m) === "decisions") return Number.isFinite(n(p.input)) ? `$${n(p.input)} per 1M input tokens · output free` : undefined;
  if (modelKind(m) === "embeddings") return Number.isFinite(n(p.input)) ? `${cardPrice(n(p.input))} per 1M input tokens` : undefined;
  if (p.audio_hour) return Number.isFinite(n(p.audio_hour)) ? `${cardPrice(n(p.audio_hour) / 60)} per min` : undefined;
  if (p.million_characters) return Number.isFinite(n(p.million_characters)) ? `${cardPrice(n(p.million_characters))} per 1M chars` : undefined;
  if (!Number.isFinite(n(p.input)) || !Number.isFinite(n(p.output))) return undefined;
  return `${cardPrice(n(p.input))} input / ${cardPrice(n(p.output))} output per 1M tokens`;
}

/** The New endpoint page, with a catalog model prefilled when the link comes from that model's card. */
export function newEndpointHref(scope: Scope, model?: string): string {
  const base = `/${encodeURIComponent(scope.org)}/${encodeURIComponent(scope.project)}/inferenceendpoints/new`;
  return model ? `${base}?model=${encodeURIComponent(model)}` : base;
}

/**
 * The origin of the inference data plane, as the CLI and the Python SDK derive it: the runtime config's
 * `inferenceURL`, else `api.<domain>` becomes `inference.<domain>`, else the API's own host. Empty means the
 * console's own origin (mock mode and the local dev proxy).
 */
export function inferenceOrigin(config: Pick<RuntimeConfig, "inferenceURL" | "apiURL">): string {
  if (config.inferenceURL) return config.inferenceURL.replace(/\/+$/, "");
  if (!config.apiURL) return "";
  const url = new URL(config.apiURL);
  if (url.hostname.startsWith("api.")) url.hostname = `inference.${url.hostname.slice("api.".length)}`;
  return url.origin;
}

/** The base URL an application calls the endpoint at (resources.md §4.5). */
export function endpointBaseURL(config: Pick<RuntimeConfig, "inferenceURL" | "apiURL">, scope: Scope, name: string): string {
  const origin = inferenceOrigin(config) || window.location.origin;
  return `${origin}/endpoints/${encodeURIComponent(scope.project)}/${encodeURIComponent(name)}/v1`;
}

export type SpecProblems = Partial<Record<"model" | "rpm" | "tpm" | "maxConcurrent" | "allowedKeys" | "maxCostUSD", string>>;

/**
 * What is wrong with a spec before the request says so, from the server's own bounds. An endpoint that already has a
 * spending limit can only raise it (`floor` is the current limit).
 */
export function specProblems(spec: EndpointSpec, floor?: string): SpecProblems {
  const out: SpecProblems = {};
  const l = spec.limits ?? {};
  if (!spec.model?.trim()) out.model = "Choose a catalog model.";
  if (l.rpm !== undefined && (l.rpm < 1 || l.rpm > LIMIT_BOUNDS.rpm)) out.rpm = `Use 1 to ${LIMIT_BOUNDS.rpm.toLocaleString("en-US")}.`;
  if (l.tpm !== undefined && l.tpm < 1) out.tpm = "Use at least 1, or leave it empty for no limit.";
  if (l.maxConcurrent !== undefined && (l.maxConcurrent < 1 || l.maxConcurrent > LIMIT_BOUNDS.maxConcurrent)) {
    out.maxConcurrent = `Use 1 to ${LIMIT_BOUNDS.maxConcurrent}.`;
  }
  if ((spec.allowedKeys?.length ?? 0) > MAX_ALLOWED_KEYS) out.allowedKeys = `Name at most ${MAX_ALLOWED_KEYS} keys.`;
  const cost = maxCostProblem(spec.maxCostUSD ?? "", floor);
  if (cost) out.maxCostUSD = cost;
  return out;
}

/**
 * What is wrong with a spending limit: it is a positive USD amount of at most six decimals, and an endpoint that
 * has one can only raise it.
 */
function maxCostProblem(value: string, floor?: string): string | undefined {
  const raise = floor ? `The limit can only be raised: it is now ${formatMoney(floor)}.` : undefined;
  if (!value) return raise;
  const micros = /^\d+(\.\d{1,6})?$/.test(value) ? parseMicros(value) : undefined;
  if (micros === undefined || micros <= 0) return "Enter an amount in dollars, such as 25 or 25.50.";
  const current = floor ? parseMicros(floor) : undefined;
  return current !== undefined && micros < current ? raise : undefined;
}

/** The merge patch that turns `before` into `after`: only changed fields, and `null` where a field is cleared. */
export function specPatch(before: EndpointSpec, after: EndpointSpec): Record<string, unknown> {
  const patch: Record<string, unknown> = {};
  if (after.model !== before.model) patch.model = after.model;
  const limits: Record<string, number | null> = {};
  for (const key of ["rpm", "tpm", "maxConcurrent"] as const) {
    if (after.limits?.[key] !== before.limits?.[key]) limits[key] = after.limits?.[key] ?? null;
  }
  if (Object.keys(limits).length) patch.limits = limits;
  const [was, now] = [before.allowedKeys ?? [], after.allowedKeys ?? []];
  if (was.length !== now.length || was.some((k, i) => k !== now[i])) patch.allowedKeys = now.length ? now : null;
  if (after.maxCostUSD !== before.maxCostUSD) patch.maxCostUSD = after.maxCostUSD ?? null;
  return patch;
}

/** Splits a typed list of API key names on commas and whitespace, dropping repeats. */
export function parseKeyNames(text: string): string[] {
  return [...new Set(text.split(/[\s,]+/).filter(Boolean))];
}
